import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Collapse,
  Flex,
  Layout,
  Row,
  Space,
  Typography,
} from "antd";
import {
  FaAddressBook,
  FaBullhorn,
  FaChartLine,
  FaCode,
  FaFileAlt,
  FaInbox,
  FaPaperPlane,
  FaRobot,
  FaUsers,
  FaWhatsapp,
} from "react-icons/fa";
import { ASSETS } from "../config/assets";
import { getPostLoginPath, getToken } from "../utils/auth";
import ThemeSwitch from "./ThemeSwitch";

const FEATURES = [
  {
    icon: FaInbox,
    title: "One shared team inbox",
    description:
      "Give agents one place to manage incoming WhatsApp conversations, reply to customers, and review conversation history.",
  },
  {
    icon: FaAddressBook,
    title: "Organized customer records",
    description:
      "Keep leads, archived contacts, tags, and customer attributes together so your team can find the right context.",
  },
  {
    icon: FaBullhorn,
    title: "Campaigns across channels",
    description:
      "Create WhatsApp broadcasts, upload audiences, launch API campaigns, and review campaign delivery reports.",
  },
  {
    icon: FaRobot,
    title: "Automation and flows",
    description:
      "Configure greeting messages, opt-in handling, chatbot flows, and WhatsApp flows to support customer journeys.",
  },
  {
    icon: FaFileAlt,
    title: "Templates for business messaging",
    description:
      "Manage WhatsApp and SMS templates, including WhatsApp template submission and approval status.",
  },
  {
    icon: FaChartLine,
    title: "Visibility into performance",
    description:
      "Review messaging analytics, campaign activity, agent activity, and usage from the workspace.",
  },
];

const WORKFLOW_STEPS = [
  {
    icon: FaWhatsapp,
    number: "01",
    title: "Connect your WhatsApp account",
    description:
      "Create a workspace and connect a WhatsApp Business Account through Meta Embedded Signup.",
  },
  {
    icon: FaUsers,
    number: "02",
    title: "Bring conversations and contacts together",
    description:
      "Organize leads and let agents work from a shared inbox with customer context.",
  },
  {
    icon: FaPaperPlane,
    number: "03",
    title: "Respond, automate, or launch campaigns",
    description:
      "Use templates, team replies, flows, and campaigns to support different customer journeys.",
  },
  {
    icon: FaChartLine,
    number: "04",
    title: "Review activity and improve",
    description:
      "Use campaign reports, analytics, and agent activity to understand how work is progressing.",
  },
];

const FAQ_ITEMS = [
  {
    key: "1",
    label: `What is ${ASSETS.brandName}?`,
    children:
      "It is a workspace for connecting a business WhatsApp account and managing customer conversations, contacts, templates, campaigns, automation, and reporting.",
  },
  {
    key: "2",
    label: "How do I connect a WhatsApp Business Account?",
    children: (
      <>
        Create an account, then follow the Meta Embedded Signup steps to choose
        a business portfolio, WhatsApp Business Account, and phone number. See
        the <Link to="/service-detail">WhatsApp setup guide</Link> for details.
      </>
    ),
  },
  {
    key: "3",
    label: "Can my team manage customer conversations together?",
    children:
      "The workspace includes a Team Inbox with agent, team role, and conversation-management screens. Available actions depend on the account and permissions configured for your workspace.",
  },
  {
    key: "4",
    label: "Can I send campaigns and automated messages?",
    children:
      "The workspace includes broadcast, spreadsheet, and API campaign screens, as well as templates and automation tools. WhatsApp messaging rules and template approval requirements still apply.",
  },
  {
    key: "5",
    label: "Where can I see plans and feature availability?",
    children:
      "Sign in to view the plan options available to your workspace. Feature access, channel availability, and usage limits depend on the selected plan.",
  },
];

function ProductOverview() {
  return (
    <figure className="home-product-visual">
      <img
        className="home-product-visual-image"
        src={ASSETS.homeHero}
        alt="Illustration showing WhatsApp Business connected with Meta and API services"
      />
      <figcaption className="home-product-visual-caption">
        <span className="home-product-visual-caption-icon">
          <FaWhatsapp aria-hidden />
        </span>
        <span>
          <strong>WhatsApp Business, connected</strong>
          <small>
            Conversations, campaigns, and customer workflows in one place.
          </small>
        </span>
      </figcaption>
    </figure>
  );
}

const Home = () => {
  const navigate = useNavigate();
  const signedIn = Boolean(getToken());
  const dashboardPath = signedIn ? getPostLoginPath() : "/login";
  const go = (path) => navigate(path);

  return (
    <div className="home-page home-page-antd home-ease">
      <Layout>
        <Layout.Header className="home-antd-header">
          <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
            <Link to="/" className="home-antd-brand">
              <img
                src={ASSETS.brandLogo}
                alt={ASSETS.brandName}
                className="home-antd-brand-logo"
              />
              <span>
                <small>WhatsApp Business Platform</small>
              </span>
            </Link>
            <Space size={8} wrap className="home-ease-nav" align="center">
              <Button type="text" href="#features">
                Features
              </Button>
              <Button type="text" href="#how-it-works">
                How it works
              </Button>
              <Button type="text" href="#integrations">
                Integrations
              </Button>

              {signedIn ? (
                <Button
                  type="primary"
                  className="home-ease-cta"
                  onClick={() => go(dashboardPath)}
                >
                  Open dashboard
                </Button>
              ) : (
                <>
                  <Button type="text" onClick={() => go("/login")}>
                    Sign in
                  </Button>
                  <Button
                    type="primary"
                    className="home-ease-cta"
                    onClick={() => go("/register")}
                  >
                    Get started
                  </Button>
                </>
              )}
              <ThemeSwitch />
            </Space>
          </Flex>
        </Layout.Header>

        <Layout.Content>
          <section className="home-antd-hero">
            <div className="home-ease-hero-glow" aria-hidden />
            <div className="home-ease-hero-row">
              <div className="home-hero-copy">
                <span className="home-ease-pill">
                  WhatsApp conversations, campaigns, and automation
                </span>
                <Typography.Title level={1} className="home-antd-title">
                  Manage customer conversations and growth from one workspace.
                </Typography.Title>
                <Typography.Paragraph className="home-antd-lead">
                  Connect your WhatsApp Business Account, bring team chats and
                  contacts together, launch campaigns, automate customer
                  journeys, and review activity in one place.
                </Typography.Paragraph>
                <Space wrap size={12} className="home-antd-actions">
                  {signedIn ? (
                    <Button
                      type="primary"
                      size="large"
                      className="home-ease-cta"
                      onClick={() => go(dashboardPath)}
                    >
                      Open dashboard
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="primary"
                        size="large"
                        className="home-ease-cta"
                        onClick={() => go("/register")}
                      >
                        Create an account
                      </Button>
                      <Button
                        size="large"
                        className="home-ease-outline"
                        onClick={() => go("/login")}
                      >
                        Sign in
                      </Button>
                    </>
                  )}
                  <Button
                    size="large"
                    className="home-ease-outline"
                    href="#features"
                  >
                    Explore features
                  </Button>
                </Space>
                <div className="home-ease-trust">
                  <span>Shared inbox for teams</span>
                  <span>Campaign and automation tools</span>
                  <span>Workspace analytics</span>
                </div>
              </div>
              <div className="home-hero-artwork">
                <div className="home-ease-visual">
                  <ProductOverview />
                </div>
              </div>
            </div>
          </section>

          <section className="home-antd-features" id="features">
            <div className="home-antd-section-inner">
              <header className="home-antd-section-head">
                <span className="home-ease-pill">One connected workspace</span>
                <Typography.Title level={2}>
                  Everything your team needs to work on WhatsApp
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  From first customer message to campaign follow-up, keep the
                  tools your team uses connected.
                </Typography.Paragraph>
              </header>
              <Row gutter={[18, 18]}>
                {FEATURES.map(({ icon: Icon, title, description }) => (
                  <Col xs={24} sm={12} lg={8} key={title}>
                    <Card className="home-feature-card">
                      <div className="home-feature-visual">
                        <span className="home-feature-icon" aria-hidden>
                          <Icon />
                        </span>
                      </div>
                      <Typography.Title level={3}>{title}</Typography.Title>
                      <Typography.Paragraph>{description}</Typography.Paragraph>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </section>

          <section className="home-antd-workflow" id="how-it-works">
            <div className="home-antd-section-inner">
              <header className="home-antd-section-head">
                <span className="home-ease-pill">How it works</span>
                <Typography.Title level={2}>
                  A clear path from setup to customer follow-up
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  Bring your channel online, coordinate the team, and use
                  campaigns and automation as your customer operations grow.
                </Typography.Paragraph>
              </header>
              <Row gutter={[18, 18]} className="home-workflow-grid">
                {WORKFLOW_STEPS.map(({ icon: Icon, ...step }) => (
                  <Col xs={24} sm={12} lg={6} key={step.number}>
                    <article className="home-workflow-step">
                      <div className="home-workflow-step-top">
                        <span className="home-workflow-step-icon">
                          <Icon aria-hidden />
                        </span>
                        <span className="home-workflow-step-number">
                          {step.number}
                        </span>
                      </div>
                      <Typography.Title level={3}>
                        {step.title}
                      </Typography.Title>
                      <Typography.Paragraph>
                        {step.description}
                      </Typography.Paragraph>
                    </article>
                  </Col>
                ))}
              </Row>
              <div className="home-workflow-guide">
                <Typography.Paragraph>
                  Need help with Meta setup, templates, or channel
                  configuration?
                </Typography.Paragraph>
                <Button
                  className="home-ease-outline"
                  onClick={() => go("/service-detail")}
                >
                  Read the WhatsApp setup guide
                </Button>
              </div>
            </div>
          </section>

          <section className="home-antd-integrations" id="integrations">
            <div className="home-antd-section-inner">
              <Row gutter={[40, 28]} align="middle">
                <Col xs={24}>
                  <span className="home-ease-pill">
                    Built for teams and systems
                  </span>
                  <Typography.Title level={2}>
                    Connect the people and tools behind every conversation
                  </Typography.Title>
                  <Typography.Paragraph type="secondary">
                    Manage agent access and roles in the workspace, and use
                    available integration tools to connect messaging operations
                    with your existing processes.
                  </Typography.Paragraph>
                </Col>
                <Col xs={24}>
                  <Row gutter={[14, 14]}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card className="home-integration-card">
                        <FaUsers aria-hidden />
                        <Typography.Title level={3}>
                          Teams and permissions
                        </Typography.Title>
                        <Typography.Paragraph>
                          Manage agents, team roles, and activity across your
                          workspace.
                        </Typography.Paragraph>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card className="home-integration-card">
                        <FaCode aria-hidden />
                        <Typography.Title level={3}>
                          APIs and webhooks
                        </Typography.Title>
                        <Typography.Paragraph>
                          Explore API documentation, API keys, webhooks, and API
                          campaign tools.
                        </Typography.Paragraph>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card className="home-integration-card">
                        <FaBullhorn aria-hidden />
                        <Typography.Title level={3}>
                          SMS and Google tools
                        </Typography.Title>
                        <Typography.Paragraph>
                          Workspace screens are available for SMS configuration
                          and Google Form and Sheets integrations.
                        </Typography.Paragraph>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card className="home-integration-card">
                        <FaChartLine aria-hidden />
                        <Typography.Title level={3}>
                          Usage and reporting
                        </Typography.Title>
                        <Typography.Paragraph>
                          Review analytics, agent activity, campaign reports,
                          and workspace usage.
                        </Typography.Paragraph>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </section>

          <section className="home-antd-plans" id="plans">
            <div className="home-antd-plans-inner">
              <div>
                <span className="home-plans-icon" aria-hidden>
                  <FaFileAlt />
                </span>
                <span className="home-ease-pill">Plans and access</span>
                <Typography.Title level={2}>
                  Choose options that fit your workspace
                </Typography.Title>
                <Typography.Paragraph>
                  Available plans, channel options, and usage limits are shown
                  in the product and may vary by workspace.
                </Typography.Paragraph>
              </div>
              <Space wrap>
                <Button
                  type="primary"
                  className="home-ease-cta"
                  onClick={() => go(signedIn ? dashboardPath : "/register")}
                >
                  {signedIn ? "Open dashboard" : "Get started"}
                </Button>
                <Button
                  className="home-ease-outline"
                  onClick={() => go("/login")}
                >
                  Sign in to view plans
                </Button>
              </Space>
            </div>
          </section>

          <section className="home-antd-faq" id="faq">
            <div className="home-antd-faq-inner">
              <header className="home-antd-faq-head">
                <span className="home-ease-pill">FAQ</span>
                <Typography.Title level={2}>
                  Frequently Asked Questions
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  A few things to know before you get started
                </Typography.Paragraph>
              </header>
              <Collapse
                accordion
                bordered={false}
                className="home-antd-faq-collapse"
                items={FAQ_ITEMS}
                defaultActiveKey={["1"]}
              />
              <Card className="home-antd-faq-more">
                <Typography.Title level={4}>
                  Need help getting set up?
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  Review the WhatsApp onboarding steps or create an account to
                  explore the workspace.
                </Typography.Paragraph>
                <Space wrap>
                  <Button
                    className="home-ease-outline"
                    onClick={() => go("/service-detail")}
                  >
                    WhatsApp setup guide
                  </Button>
                  <Button
                    type="primary"
                    className="home-ease-cta"
                    onClick={() => go("/register")}
                  >
                    Get started
                  </Button>
                </Space>
              </Card>
            </div>
          </section>
        </Layout.Content>

        <Layout.Footer className="home-antd-footer">
          <div className="home-site-footer">
            <div className="home-site-footer-grid">
              <div className="home-site-footer-brand">
                <Link to="/" className="home-site-footer-logo">
                  <img src={ASSETS.brandLogo} alt={ASSETS.brandName} />
                </Link>
                <p>
                  A workspace for WhatsApp customer conversations, campaigns,
                  automation, and team operations.
                </p>
              </div>
              <div className="home-site-footer-col">
                <h4>Product</h4>
                <ul>
                  <li>
                    <a href="#features">Features</a>
                  </li>
                  <li>
                    <a href="#plans">Plans</a>
                  </li>
                  <li>
                    <Link to={signedIn ? dashboardPath : "/login"}>
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <a href="#faq">FAQ</a>
                  </li>
                </ul>
              </div>
              <div className="home-site-footer-col">
                <h4>Resources</h4>
                <ul>
                  <li>
                    <Link to="/service-detail">WhatsApp setup guide</Link>
                  </li>
                  <li>
                    <a
                      href={ASSETS.brandDomain}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Company website
                    </a>
                  </li>
                </ul>
              </div>
              <div className="home-site-footer-col">
                <h4>Legal</h4>
                <ul>
                  <li>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/terms-and-conditions">Terms of Service</Link>
                  </li>
                  <li>
                    <Link to="/refund-policy">Refund Policy</Link>
                  </li>
                  <li>
                    <Link to="/security">Security</Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="home-site-footer-bottom">
              <p>
                © {new Date().getFullYear()} {ASSETS.brandName}. All rights
                reserved.
              </p>
            </div>
          </div>
        </Layout.Footer>
      </Layout>
    </div>
  );
};

export default Home;
